import { datadogRum } from '@datadog/browser-rum'
import { featureFlags } from '@seen/core/featureFlags'

const FACESHEET_TABS = ['for-you', 'highlights', 'contact', 'memo'] as const
export type FacesheetTab = (typeof FACESHEET_TABS)[number]

export function isFacesheetTab(value: string): value is FacesheetTab {
  return FACESHEET_TABS.includes(value as FacesheetTab)
}

// Stable card ids. Card titles can't stand in for them: the Contact tab titles
// its cards with the participant's and related people's names.
export type FacesheetCardName =
  | 'observations'
  | 'conditions'
  | 'medications'
  | 'allergies'
  | 'diet'
  | 'insurance'
  | 'social_history'
  | 'service_allocation'
  | 'additional_information'
  | 'notepad'
  | 'my_notepad'
  | 'participant'
  | 'emergency_contacts'
  | 'care_team'
  | 'related_person'

// Shape-only fields: bare ids, tab and card ids, counts. Never names or
// observation values.
interface FacesheetActionFields {
  participantId?: string
  tab?: FacesheetTab
}

// Datadog query: `@action.name:"facesheet.tab.selected"`
export function reportFacesheetTabSelected(
  fields: FacesheetActionFields & { tab: FacesheetTab }
): void {
  datadogRum.addAction('facesheet.tab.selected', { ...fields })
}

// Header-row toggles only; the collapse-all hotkeys don't report here.
// Datadog query: `@action.name:"facesheet.card.toggled"`
export function reportFacesheetCardToggled(
  fields: FacesheetActionFields & {
    card: FacesheetCardName
    state: 'expanded' | 'collapsed'
  }
): void {
  datadogRum.addAction('facesheet.card.toggled', { ...fields })
}

// The Observations card title, which links to the all-observations page.
// Datadog query: `@action.name:"facesheet.observations.title_clicked"`
export function reportFacesheetObservationsTitleClicked(
  fields: FacesheetActionFields
): void {
  datadogRum.addAction('facesheet.observations.title_clicked', { ...fields })
}

// `count` is the N in "See all N".
// Datadog query: `@action.name:"facesheet.observations.see_all_clicked"`
export function reportFacesheetObservationsSeeAllClicked(
  fields: FacesheetActionFields & { count: number }
): void {
  datadogRum.addAction('facesheet.observations.see_all_clicked', { ...fields })
}

// Pins belong to the practitioner, not the participant, so pin actions carry
// the observation's code but no participant id: together they would say which
// test was of interest on whose chart. `pinCount` is the number of visible
// pins once the change is saved.
interface FacesheetPinFields {
  code: string
  system: string
  pinCount: number
}

// Datadog query: `@action.name:"facesheet.observations.pin_added"`
export function reportFacesheetObservationPinAdded(
  fields: FacesheetPinFields
): void {
  datadogRum.addAction('facesheet.observations.pin_added', { ...fields })
}

// `via` is the + menu checklist or the row's x.
// Datadog query: `@action.name:"facesheet.observations.pin_removed"`
export function reportFacesheetObservationPinRemoved(
  fields: FacesheetPinFields & { via: 'menu' | 'row' }
): void {
  datadogRum.addAction('facesheet.observations.pin_removed', { ...fields })
}

// Datadog query: `@action.name:"facesheet.observations.pins_reordered"`
export function reportFacesheetObservationPinsReordered(fields: {
  pinCount: number
}): void {
  datadogRum.addAction('facesheet.observations.pins_reordered', { ...fields })
}

export type FacesheetObservationSection = 'general' | 'pinned' | 'recent'

// Datadog query: `@action.name:"facesheet.observations.trend_opened"`
export function reportFacesheetObservationTrendOpened(
  fields: FacesheetActionFields & { section: FacesheetObservationSection }
): void {
  datadogRum.addAction('facesheet.observations.trend_opened', { ...fields })
}

// Fired when a save succeeds.
// Datadog query: `@action.name:"facesheet.notepad.saved"`
export function reportFacesheetNotepadSaved(
  fields: FacesheetActionFields & { notepad: 'shared' | 'mine' }
): void {
  datadogRum.addAction('facesheet.notepad.saved', { ...fields })
}

// The Shift+Left and Shift+Right collapse-all and expand-all hotkeys.
// Datadog query: `@action.name:"facesheet.cards.toggled_all"`
export function reportFacesheetCardsToggledAll(
  fields: FacesheetActionFields & { state: 'expanded' | 'collapsed' }
): void {
  datadogRum.addAction('facesheet.cards.toggled_all', { ...fields })
}

// Feature flag evaluations live on the current RUM view, so this has to run
// again on every view for each to carry the value.
// Datadog query: `@feature_flags.facesheet-improvements:true`
export function recordFacesheetImprovementsFlag(enabled: boolean): void {
  datadogRum.addFeatureFlagEvaluation(
    featureFlags.facesheetImprovements.key,
    enabled
  )
}
